import type { InventoryListByIdAPI } from '@openx/types/inventoryList.ts';
import {
  AppInventoryOption,
  type AppInventoryOptionType,
  type AppInventoryState,
  AppInventoryVersion,
} from '@openx/types/targeting/appInventory.ts';
import {
  AppInventoryType,
  type InstanceProps,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent.ts';
import type { TargetingProps } from '@openx/types/targeting/targeting.ts';
import type { TargetingAppInventory } from '@openx/types/targeting/targetingAppInventoryTypes.ts';
import { type ComparisonMap, ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import isEqual from 'lodash/isEqual';
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import type { UnknownAction } from 'redux';
import { updateMultiParameterCategories } from '../../../utils/hooks/updateMultiParameterCategories.ts';
import { useCustomOptionsContext } from '../../../utils/hooks/useCustomOptionsContext.tsx';
import { useInstanceState } from '../../InventoryAndContent/hooks/useInstanceState.ts';
import {
  deriveBundleIdComparisonType,
  deriveComparisonType,
} from '../../InventoryAndContent/InventoryAndContentItems/utils.ts';
import { getEmptyInventoryState, resetContent, setContent } from '../../shared/AppInventory/inventoryHelpers.ts';
import { appInventoryStateToApi } from '../../shared/AppInventory/utils.ts';
import type { InvalidItemsError } from '../../shared/BulkAdd/types.ts';
import { usePreselectableDrawer } from '../../shared/hooks/usePreselectableDrawer.ts';
import { useAppInventoryState } from '../../shared/InventoryAndContent/hooks/useAppInventoryState.ts';
import { useDrawer } from '../../shared/TargetingDrawer/useDrawer.tsx';
import { useAppInventory } from './useAppInventory.ts';

type UseAppInventoryDataProps = Omit<TargetingProps<TargetingAppInventory>, 'targetingParams'> & {
  initState: AppInventoryState;
  setSelectedInventory: Dispatch<SetStateAction<AppInventoryState | null>>;
};

export const useAppInventoryData = ({
  onFieldUpdate = noop,
  field,
  initState,
  setSelectedInventory,
  inventoryVersion,
}: UseAppInventoryDataProps) => {
  const [userChangedInitialValue, setUserChangedInitialValue] = useState(false);
  const { inventoryState, dispatchInventory } = useAppInventory(initState);

  const [inputInstanceValue, setInputInstanceValue] = useState<InstanceProps | null>(null);
  const { instanceState, dispatchInstanceState } = useInstanceState('');

  const isDataUpdated = useMemo(() => !isEqual(initState, inventoryState), [initState, inventoryState]);

  const [appBundleIdDuplicateErrors, setAppBundleIdDuplicateErrors] = useState<InvalidItemsError | null>(null);
  const [inventoryListItems, setInventoryListItems] = useState<InventoryListByIdAPI['values']>();

  const topBoxCustomOptions = useCustomOptionsContext<AppInventoryOptionType>();

  const {
    setAppNameComparisonType,
    setAppInventoryType,
    setContentOption,
    setBundleIdComparisonType,
    isAppInventoryAllowed,
    contentOption,
    appInventoryType,
    appNameComparisonType,
    bundleIdComparisonType,
  } = useAppInventoryState<AppInventoryOptionType>({ topBoxCustomOptions, initState });

  const onFieldUpdateExtended = useCallback(
    (...args: Parameters<typeof onFieldUpdate>) => {
      setUserChangedInitialValue(true);
      onFieldUpdate(...args);
    },
    [onFieldUpdate]
  );

  const onApplyChanges = useCallback(() => {
    setSelectedInventory(inventoryState);
    onFieldUpdateExtended(field, appInventoryStateToApi(inventoryState));
  }, [field, onFieldUpdateExtended, setSelectedInventory, inventoryState]);

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => onApplyChanges(),
    restoreParams: () => {
      setInputInstanceValue(null);
      dispatchInventory({ payload: initState, type: setContent } as unknown as UnknownAction);
      setAppNameComparisonType(
        deriveComparisonType(initState[InventoryContentOption.APP_GROUP]?.val as ComparisonMap | undefined)
      );
      setBundleIdComparisonType(
        deriveBundleIdComparisonType(
          initState[InventoryContentOption.APP_BUNDLE_ID]?.val as ComparisonMap | undefined,
          initState[InventoryContentOption.APP_GROUP]?.val as ComparisonMap | undefined
        )
      );
    },
  });

  const onRemoveAll = useCallback(() => {
    onFieldUpdateExtended(field, null);
    setSelectedInventory(null);
    dispatchInventory({ type: resetContent } as unknown as UnknownAction);
  }, [dispatchInventory, field, onFieldUpdateExtended, setSelectedInventory]);

  const onRemoveParameter = useCallback(
    (option: AppInventoryOptionType) => {
      const empty = getEmptyInventoryState();

      const nextState: AppInventoryState = {
        ...inventoryState,
        [option]: empty[option],
        ...(option === AppInventoryOption.APP_GROUP && isAppInventoryAllowed
          ? { [AppInventoryOption.APP_BUNDLE_ID]: empty[AppInventoryOption.APP_BUNDLE_ID] }
          : {}),
      };

      setSelectedInventory(nextState);

      dispatchInventory({
        payload: nextState,
        type: setContent,
      } as unknown as UnknownAction);

      updateMultiParameterCategories(field, onFieldUpdateExtended, nextState, state => appInventoryStateToApi(state));
    },
    [inventoryState, field, onFieldUpdateExtended, setSelectedInventory, dispatchInventory, isAppInventoryAllowed]
  );

  const applyNextState = useCallback(
    (nextState: AppInventoryState) => {
      setSelectedInventory(nextState);
      dispatchInventory({
        payload: nextState,
        type: setContent,
      } as unknown as UnknownAction);
      onFieldUpdateExtended(field, appInventoryStateToApi(nextState));
    },
    [setSelectedInventory, dispatchInventory, onFieldUpdateExtended, field]
  );

  const onRemoveAppName = useCallback(() => {
    const empty = getEmptyInventoryState();
    const nextState: AppInventoryState = {
      ...inventoryState,
      [AppInventoryOption.APP_GROUP]: empty[AppInventoryOption.APP_GROUP],
    };

    setAppNameComparisonType(ComparisonType.EQUALS);
    applyNextState(nextState);
  }, [inventoryState, setAppNameComparisonType, applyNextState]);

  const onRemoveBundleIdManual = useCallback(() => {
    const currentBundleId = inventoryState[AppInventoryOption.APP_BUNDLE_ID];
    const empty = getEmptyInventoryState()[AppInventoryOption.APP_BUNDLE_ID];

    const nextState: AppInventoryState = {
      ...inventoryState,
      [AppInventoryOption.APP_BUNDLE_ID]: {
        ...empty,
        inventorylist_id: currentBundleId.inventorylist_id ?? empty.inventorylist_id,
      },
    };

    // App Bundle ID inherits App Name's mode once it has no items of its own. Resetting it to
    // Allow while App Name still blocks yields a combination the API rejects.
    setBundleIdComparisonType(
      deriveBundleIdComparisonType(
        nextState[AppInventoryOption.APP_BUNDLE_ID].val as ComparisonMap | undefined,
        nextState[AppInventoryOption.APP_GROUP]?.val as ComparisonMap | undefined
      )
    );
    applyNextState(nextState);
  }, [inventoryState, setBundleIdComparisonType, applyNextState]);

  const onRemoveBundleIdInventoryList = useCallback(() => {
    const currentBundleId = inventoryState[AppInventoryOption.APP_BUNDLE_ID];
    const empty = getEmptyInventoryState()[AppInventoryOption.APP_BUNDLE_ID];

    const nextState: AppInventoryState = {
      ...inventoryState,
      [AppInventoryOption.APP_BUNDLE_ID]: {
        ...currentBundleId,
        inventorylist_id: empty.inventorylist_id,
      },
    };

    setInventoryListItems(undefined);
    applyNextState(nextState);
  }, [inventoryState, applyNextState]);

  const isValidFormat = (type: string): type is AppInventoryOptionType => {
    return Object.values(AppInventoryOption).includes(type as AppInventoryOptionType);
  };

  const openDrawerWithPreselectedType = usePreselectableDrawer<AppInventoryOptionType>(
    isValidFormat,
    onDrawerOpen,
    setContentOption
  );

  const isValidAppFormat = (type: string): type is AppInventoryType => {
    return Object.values(AppInventoryType).includes(type as AppInventoryType);
  };

  const openAppInventoryDrawerWithPreselectedType = usePreselectableDrawer<AppInventoryType>(
    isValidAppFormat,
    onDrawerOpen,
    setAppInventoryType
  );

  return {
    userChangedInitialValue,
    inventoryState,
    dispatchInventory,
    inputInstanceValue,
    instanceState,
    dispatchInstanceState,
    isDataUpdated,
    appBundleIdDuplicateErrors,
    setAppBundleIdDuplicateErrors,
    inventoryListItems,
    setInventoryListItems,
    contentOption,
    setContentOption,
    appInventoryType,
    setAppInventoryType,
    appNameComparisonType,
    setAppNameComparisonType,
    bundleIdComparisonType,
    setBundleIdComparisonType,
    isOpen,
    onDrawerClose,
    onDrawerApply,
    onRemoveAll,
    onRemoveParameter,
    onRemoveAppName,
    onRemoveBundleIdManual,
    onRemoveBundleIdInventoryList,
    onApplyChanges,
    openDrawerWithPreselectedType:
      inventoryVersion === AppInventoryVersion.APP_INVENTORY_ONLY_V2
        ? openAppInventoryDrawerWithPreselectedType
        : openDrawerWithPreselectedType,
  };
};
